import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type CurrencyCode = 'USD' | 'TWD'

interface CurrencyContextValue {
  currency: CurrencyCode
  /** 根據幣別格式化價格，傳入 USD 與 TWD 兩個值，由 context 決定顯示哪個 */
  formatProductPrice: (price: number, priceTwd?: number) => string
  /** 格式化已確定幣別的金額（用於小計等自行計算的場合） */
  formatAmount: (amount: number) => string
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined)

const SESSION_KEY = 'cs_detected_currency'

async function detectCurrency(): Promise<CurrencyCode> {
  try {
    const cached = sessionStorage.getItem(SESSION_KEY)
    if (cached === 'USD' || cached === 'TWD') return cached as CurrencyCode

    const res = await fetch('https://api.country.is/', {
      signal: AbortSignal.timeout(4000),
    })
    const data = (await res.json()) as { country?: string }
    const code: CurrencyCode = data.country === 'TW' ? 'TWD' : 'USD'
    sessionStorage.setItem(SESSION_KEY, code)
    return code
  } catch {
    return 'USD'
  }
}

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [currency, setCurrency] = useState<CurrencyCode>('USD')

  useEffect(() => {
    detectCurrency().then(setCurrency)
  }, [])

  const formatProductPrice = (price: number, priceTwd?: number): string => {
    if (currency === 'TWD' && priceTwd && priceTwd > 0) {
      return `NT$${Math.round(priceTwd).toLocaleString()}`
    }
    return `$${price.toFixed(1)}`
  }

  const formatAmount = (amount: number): string => {
    if (currency === 'TWD') {
      return `NT$${Math.round(amount).toLocaleString()}`
    }
    return `$${amount.toFixed(1)}`
  }

  return (
    <CurrencyContext.Provider value={{ currency, formatProductPrice, formatAmount }}>
      {children}
    </CurrencyContext.Provider>
  )
}

export const useCurrency = (): CurrencyContextValue => {
  const ctx = useContext(CurrencyContext)
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider')
  return ctx
}
